/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import { gql, useApolloClient, useMutation } from '@apollo/client';
import { useSessionContext } from '@exo/frontend-common-session-context';
import { useProfileModification } from '@exo/frontend-features-tdaccount-logic';
import { Address, cleanAddress } from '../model/address';

export const GQL_SET_SHIPPING_INFO = gql`
mutation CartSetShippingInfo($cartId: ID!, $addressId: ID, $shipModeId: ID) {
  cartSetShippingInfo(cartId: $cartId, addressId: $addressId, shipModeId: $shipModeId)
}

`;

const GQL_ADD_PAYMENT_METHOD = gql`
  mutation CartUpdatePaymentMethod(
    $addressId: ID
    $cartId: ID!
    $address: CusAddressInput
    $paymentMethodId: ID!
    $attributes: [CrtPaymentAttributeInput]
  ) {
    cartAddPaymentInstruction(
      cartId: $cartId
      addressId: $addressId
      address: $address
      paymentMethodId: $paymentMethodId
      attributes: $attributes
    )
  }
`;

const GQL_DELETE_PAYMENT_METHODS = gql`
  mutation CartClearPaymentMethods($cartId: ID!) {
    cartDeletePaymentInstructions(cartId: $cartId)
  }
`;

const GQL_CHECKOUT = gql`
mutation PmCheckout($input: PMCheckoutInput!) {
  pmCheckout(input: $input) {
    orderId
  }
}


`;

const GQL_GET_CART = gql`
query CartById($id: ID) {
  me {
    id
    carts(cartId: $id) {
      id
      grandTotal {
        ...Currency
      }
      totalProductPrice {
        ...Currency
      }
      totalShippingCharge {
        ...Currency
      }
      totalAdjustment {
        ...Currency
      }
      totalShippingTax {
        ...Currency
      }
      totalSalesTax {
        ...Currency
      }
      adjustments {
        title
        amount {
          ...Currency
        }
      }
      shippingInfo {
        shipMode {
          id
          identifier
        }
        pickupInStore {
          id
          name
        }
        shippingAddress {
          id
          country
          zip
        }
      }
      lineItems {
        id
        quantity
        partnumber
        linePrice {
          ...Currency
        }
        unitPrice {
          ...Currency
        }
        product {
          id
          type
          thumbnail
          name
          partnumber
          topParentCatName
          availability {
            availableQuantity
          }
          price {
            list {
              ...Currency
            }
            offer {
              ...Currency
            }
          }
        }
      }
      availableShippingModes {
        id
        identifier
        description
        type
        shippingRate {
          value
          currency
        }
      }
    }
  }
}

fragment Currency on CmmMoney {
  value
  currency
}
`;

const GQL_LEXIS_NEXIS = gql`
  query Fraudcheck{
    fraudcheck{
      errorKey
      success
    }
  }
`;

const GQL_TD_STORES = gql`
  query Tdstores($filter: StStoreFilter!) {
    tdstores(filter: $filter) {
      id
      address
      latitude
      longitude
      phoneNo
      country
      hours {
        Monday
        Tuesday
        Wednesday
        Thursday
        Friday
        Saturday
        Sunday
      }
    }
  }
`;
const GQL_CART_ADD_PAYMNET_INSTRUCTION = gql`
mutation addPI($cartId: ID!, $paymentMethodId: ID!, $attributes: [CrtPaymentAttributeInput!], $addressId: ID, $amount: String) {
  cartAddPaymentInstruction(cartId: $cartId, paymentMethodId: $paymentMethodId, attributes: $attributes, addressId: $addressId, amount: $amount)
}
`;

const GQL_UPDATE_CUSTOMER_IP = gql`mutation UpdateCustomerIP($input: CartCustomerMetrics!) {
  updateCustomerIP(input: $input) {
    orderId
  }
}`;

const GQL_DELETE_PI_ON_CART = gql`
mutation deletePIOnCart($cartId: ID!) {
  cartDeletePaymentInstructions(cartId: $cartId)
}`;
const GQL_START_CHECKOUT = gql`
mutation StartCheckout {
  startCheckout {
    isPriceRefreshed
    orderId
  }
}

`
const GQL_MIXED_CART_CHECK = gql`
mutation PmMixedCartCheck {
  pmMixedCartCheck {
    hasPhysicalProducts
    hasPooledProducts
  }
}

`
const GQL_PRECHECKOUT = gql`
mutation PmPrecheckout($orderId: String!) {
  pmPrecheckout(orderId: $orderId) {
    orderId
  }
}
`

const GQL_REMOVE_SELL_JUNK_ORDER = gql`
mutation RemoveSellJunkOrder {
  removeSellJunkOrder {
    removeSellJunkOrder
  }
}
`

const GQL_PUNCHOUT_PAYMENTINFO = gql`
  mutation GetPunchoutPaymentInfo($input: PunchoutPaymentInfoInput!) {
  getPunchoutPaymentInfo(input: $input) {
    punchoutPopupURL
  }
}
`;

const GQL_PUNCHOUT_PAYMENTCALLBACK = gql`
  mutation PunchoutPaymentCallBack($input: PunchoutPaymentCallBackInput!) {
  PunchoutPaymentCallBack(input: $input) {
    punchoutTranResult
  }
}
`;

// TODO: There's some opportunity to reduce duplication as some of this is handled in useCartModification
export const useCheckout = () => {
  const apolloClient = useApolloClient();
  const session = useSessionContext();
  const { addAddress, updateAddress, updateDefaultAddress } = useProfileModification();

  const [gqlAddPaymentMethod] = useMutation(GQL_ADD_PAYMENT_METHOD);
  const [gqlDeletePaymentMethods] = useMutation(GQL_DELETE_PAYMENT_METHODS);
  const [gqlCheckout] = useMutation(GQL_CHECKOUT);
  const [gqlSetShippingInfo] = useMutation(GQL_SET_SHIPPING_INFO);
  const [gqlAddCheckoutPaymentMethod] = useMutation(GQL_CART_ADD_PAYMNET_INSTRUCTION);
  const [gqlUpdateCustomerIP] = useMutation(GQL_UPDATE_CUSTOMER_IP);
  const [gqlDeletePiOnCart] = useMutation(GQL_DELETE_PI_ON_CART);
  const [gqlStartCheckout] = useMutation(GQL_START_CHECKOUT);
  const [gqlMixedCartCheck] = useMutation(GQL_MIXED_CART_CHECK);
  const [gqlPreCheckout] = useMutation(GQL_PRECHECKOUT);
  const [gqlRemoveSellJunkOrder] = useMutation(GQL_REMOVE_SELL_JUNK_ORDER)
  const [gqlPunchoutPaymentInfo] = useMutation(GQL_PUNCHOUT_PAYMENTINFO);
  const [gqlPunchoutPaymentCallBack] = useMutation(GQL_PUNCHOUT_PAYMENTCALLBACK);

  const cartId = session?.cartId;

  return {
    saveShippingInfo: async (props: {
      shipModeId?: string;
      address?: Address;
      addressId?: string;
      storeId?: string;
    }) => {
      console.assert(props.address || props.addressId);

      const variables: any = { cartId };
      if (props.addressId) variables.addressId = props.addressId;
      if (props.storeId) variables.storeId = props.storeId;
      if (props.shipModeId) variables.shipModeId = props.shipModeId;
      if (props.address) variables.address = cleanAddress(props.address);

      const res = await gqlSetShippingInfo({ variables });
      return res;
    },

    saveToAddressBook: async (address: Address) => {
      let addressClean = cleanAddress(address);
      addressClean = {
        ...addressClean,
        name: address.name,
        storeField1: address.storeField1,
        phone2: address.phone2,
        addressType: address.addressType
      }
      
      const addressId = address.id;
      if (!addressId) {
        const res = await addAddress({
          ...addressClean,
        });

        return res.data.customerAddAddress;
      } else {
        const res = await updateAddress(address.id!, addressClean);
        return res.data.customerUpdateAddress;
      }
    },
    savePaymentInfo: async (props: {
      addressId?: string;
      address?: Address;
      paymentMethodId: string;
      attributes?: { name: string; value: string }[];
      cartId?: string;
      amount?: string;
    }) => {
      await gqlDeletePaymentMethods({ variables: { cartId } });

      const variables: any = {
        cartId,
        paymentMethodId: props.paymentMethodId,
        addressId: props?.addressId,
        amount: props?.amount,
        attributes: props.attributes ?? []
      };

      if (props.address) variables.address = cleanAddress(props.address);
      if (props.addressId) variables.addressId = props.addressId;

      return gqlAddPaymentMethod({ variables });
    },
    savePaymentInfoNew: async (props: {
      cartId?: string;
      paymentMethodId: string;
      addressId: string;
      amount: string;
      attributes?: { name: string; value: string }[];
    }) => {

      const variables: any = {
        cartId: cartId,
        paymentMethodId: props?.paymentMethodId,
        addressId: props?.addressId,
        amount: props?.amount,
        attributes: props?.attributes
      }

      let customerIP;
      await fetch('https://api.ipify.org?format=json')
        .then(response => response.json())
        .then(data => {
          customerIP = data.ip;
        })
        .catch(error => {
          console.log('Error:', error);
        });

      const variablesCustIp: any = {
        "ip": customerIP ? customerIP.toString() : "0.0.0.0",
        "userAgent": navigator?.userAgent,
        "accept": "*/*",
        "browserLanguage": navigator?.language,
        "timeZone": new Date().getTimezoneOffset().toString(),
        "javaEnabled": navigator?.javaEnabled(),
        "javascriptEnabled": true,
        "colorDepth": screen.colorDepth,
        "screenHeight": screen.height,
        "screenWidth": screen.width
      }

      await gqlUpdateCustomerIP({
        variables: {
          input: variablesCustIp
        }
      });
      const res = await gqlAddCheckoutPaymentMethod({ variables });
      return res;
    },
    getCartForSummary: async (id) => {
      return apolloClient.query({
        query: GQL_GET_CART,
        fetchPolicy: 'network-only',
        variables: { id }
      });
    },

    placeOrder: async (id, props: {
      paymentMethodId: string;
    }) => {
      const res = await gqlCheckout({
        variables: {
          "input": {
            "orderId": id,
            "piId": props.paymentMethodId
          }
        }
      })
      return res?.data?.checkout ? res?.data?.checkout : res?.data?.pmCheckout;
    },

    lexisNexisVerification: async () => {
      return apolloClient.query({
        query: GQL_LEXIS_NEXIS,
        fetchPolicy: 'network-only',
        variables: {}
      });
    },

    filteredTDStores: async (lat, lng) => {
      return apolloClient.query({
        query: GQL_TD_STORES,
        fetchPolicy: 'network-only',
        variables: { filter: { latitude: lat.toString(), longitude: lng.toString() } }
      })
    },

    onDefaultAddressUpdate: async (address: Address) => {
      // TODO: To remove if not required
      // console.assert(session.type === 'USER');
      const res = await updateDefaultAddress(address);
      return res;
    },

    UpdateCustomerIP: async (variablesCustIp) => {
      const res = await gqlUpdateCustomerIP({
        variables: {
          input: variablesCustIp
        }
      });
      return res?.data?.updateCustomerIP?.orderId;
    },
    deletePiOnCart: async (id) => {
      const res = await gqlDeletePiOnCart({ variables: { cartId: id } })
      return res?.data?.cartDeletePaymentInstructions;
    },
    startCheckout: async () => {
      const res = await gqlStartCheckout()
      return res?.data
    },
    mixedCartCheck: async () => {
      const res = await gqlMixedCartCheck()
      return res?.data
    },
    precheckout: async (id) => {
      const res = await gqlPreCheckout({ variables: { orderId: id } })
      return res?.data
    },
    removeSellJunkOrder: async() => {
      const res = await gqlRemoveSellJunkOrder()
      return res?.data
    },

    getPunchoutPaymentInfo: async (id, pId) => {
      const res = await gqlPunchoutPaymentInfo({
        variables: {
          "input": {
            "orderId": id,
            "piId": pId,
            "profileName": "IBM_PunchoutPaymentInfo"
          }
        }
      })

      
      return res?.data?.getPunchoutPaymentInfo?.punchoutPopupURL;
    },

    punchoutPaymentCallBack: async (id,langId, piId,cres,threeDSSessionData) => {
      const res = await gqlPunchoutPaymentCallBack({
        variables: {
          "input": {
            "orderId": id,
            "langId": langId,
            "piId": piId,
            "token": piId,
            "cres": cres,
            "threeDSSessionData": threeDSSessionData
          }
        }
      })
      
      return res;
    },
  };
};
